import {useContext} from "react";

import {useSelect, useDispatch} from '@wordpress/data';
import {__, sprintf} from "@wordpress/i18n";

import {VouchersFormContext} from "./vouchers-form-context";

export const useStoreCartVouchers = () => {
	const {
        vouchersForm,
        setVouchersForm,
        appliedVouchersList,
        setAppliedVouchersList,
        appliedGiftCardsList,
        setAppliedGiftCardsList,
        appliedPromotionsList,
        setAppliedPromotionsList,
        giftCardInFlight,
        setGiftCardInFlight,
        validPromotions,
        setValidPromotions,
        allPromotions,
        setAllPromotions,
        isLoading,
        setIsLoading
    } = useContext(VouchersFormContext);

	useSelect(
		( select ) => {
			const store = select( 'voucherify' );

            Promise.all([
                store.getAppliedVouchers(),
                store.getAppliedGiftCards(),
                store.getAppliedPromotions(),
                store.getAvailablePromotions()
            ]).then(([
                appliedVouchers,
                appliedGiftCards,
                appliedPromotions,
                promotions
            ]) => {
                setAppliedVouchersList(appliedVouchers);
                setAppliedGiftCardsList(appliedGiftCards);
                setAppliedPromotionsList(appliedPromotions);
                setValidPromotions(promotions.valid ?? []);
                setAllPromotions(promotions.all ?? []);

                setIsLoading(false);
            });
		}
	);

	const displayGiftCardForm = (code, balance, balanceLocalized) => {
        setGiftCardInFlight({code, balance, balanceLocalized})
        setVouchersForm('gift');
        return Promise.resolve();
    }

	const displayRegularForm = () => {
        setGiftCardInFlight({});
        setVouchersForm('regular');
        return Promise.resolve();
    }

	const getAppliedVouchersList = () => Object.entries(appliedVouchersList ?? {});

    const getAppliedGiftCardsList = () => Object.entries(appliedGiftCardsList ?? {});

    const getAppliedPromotionsList = () => Object.entries(appliedPromotionsList ?? {});

	const isAnyVoucherApplied = () => getAppliedVouchersList().length > 0;

	const isAnyPromotionApplied = () => getAppliedPromotionsList().length > 0;

	const isAnyGiftCardApplied = () => getAppliedGiftCardsList().length > 0;

	const getFormType = () => vouchersForm === 'gift' ? 'gift' : 'regular';

    const removeAllVouchers = () => {
        setIsLoading(true);

        const promises = getAppliedVouchersList()
            .map(([voucherCode]) => removeVoucherWithNotices(voucherCode));
        setAppliedVouchersList({});

        return Promise.all(promises)
            .then(promises => {
                setIsLoading(false);
                return promises;
            })
            .catch(error => {
                setIsLoading(false);
                throw error;
            });
    };

    const removeAllGiftCards = () => {
        setIsLoading(true);

        const promises = getAppliedGiftCardsList()
            .map(([voucherCode]) => removeGiftCardWithNotices(voucherCode));
        setAppliedGiftCardsList({});

        return Promise.all(promises)
            .then(promises => {
                setIsLoading(false);
                return promises;
            })
            .catch(error => {
                setIsLoading(false);
                throw error;
            });
    };

    const removeAllPromotions = () => {
        setIsLoading(true);

        const promises = getAppliedPromotionsList()
            .map(([promotionCode, promotionData]) => removePromotionWithNotices(promotionCode, promotionData.name));
        setAppliedPromotionsList({});

        return Promise.all(promises)
            .then(promises => {
                setIsLoading(false);
                return promises;
            })
            .catch(error => {
                setIsLoading(false);
                throw error;
            });;
    };

    const { addPromotion, addGiftCard, maybeAddVoucher, removeVoucher, removeGiftCard, removePromotion } = useDispatch( 'voucherify' );
	const { createNotice } = useDispatch( 'core/notices' );

	const removeVoucherWithNotices = (voucherCode) => {
        return removeVoucher(voucherCode)
            .then(() => {
                createNotice(
                    'info',
                    sprintf(
                        /* translators: %s coupon code. */
                        __(
                            'Coupon code "%s" has been removed from your cart.',
                            'woocommerce'
                        ),
                        voucherCode
                    ),
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(true);
            })
            .catch((error) => {
                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
    }

    const removeGiftCardWithNotices = (voucherCode) => {
        return removeGiftCard(voucherCode)
            .then(() => {
                createNotice(
                    'info',
                    sprintf(
                        /* translators: %s coupon code. */
                        __('Gift card "%s" has been removed from your cart.', 'voucherify'),
                        voucherCode
                    ),
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(true);
            })
            .catch((error) => {
                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
    }

    const removePromotionWithNotices = (promotionCode, promotionName) => {
        return removePromotion(promotionCode)
            .then(() => {
                createNotice(
                    'info',
                    sprintf(
                        /* translators: %s coupon code. */
                        __('Promotion "%s" has been removed from your cart.', 'voucherify'),
                        promotionName ?? promotionCode
                    ),
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(true);
            })
            .catch((error) => {
                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
    };

    const createCustomNotice = (message) => createNotice(
        'info',
        message,
        {
            id: 'coupon-form',
            type: 'snackbar',
            context: 'wc/cart',
        }
    );

	const addVoucherToList = (voucherCode) => {
        setIsLoading(true);

        return maybeAddVoucher(voucherCode)
            .then((voucher) => {
                setIsLoading(false);

                if (voucher.type !== 'gift') {
                    createCustomNotice(sprintf(
                        __('Coupon code "%s" has been applied.', 'voucherify'),
                        voucher.code
                    ));
                    setAppliedVouchersList({...appliedVouchersList, ...{[voucher.code]: voucher.amount}});
                }

                return Promise.resolve(voucher);
            })
            .catch((error) => {
                setIsLoading(false);

                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
	};

    const addGiftCardToList = (code, amount) => {
        setIsLoading(true);

        return addGiftCard(code, amount)
            .then((voucher) => {
                setIsLoading(false);

                createCustomNotice(sprintf(
                    __('Gift card "%s" has been applied with amount %s.', 'voucherify'),
                    voucher.code,
                    voucher.amount
                ));
                setAppliedGiftCardsList({...appliedGiftCardsList, ...{[voucher.code]: voucher.amount}});
                setGiftCardInFlight({});
                setVouchersForm('regular');

                return Promise.resolve(voucher);
            })
            .catch((error) => {
                setIsLoading(false);

                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
    };

    const addPromotionsToList = (code) => {
        setIsLoading(true);

        return addPromotion(code)
            .then((voucher) => {
                setIsLoading(false);

                createCustomNotice(sprintf(
                    __('Promotion "%s" has been applied.', 'voucherify'),
                    voucher.name ?? voucher.code
                ));
                setAppliedPromotionsList({...appliedPromotionsList, ...{
                    [voucher.code]: {
                        amount: voucher.amount,
                        name: voucher.name
                    }
                }});

                return Promise.resolve(voucher);
            })
            .catch((error) => {
                setIsLoading(false);

                createNotice(
                    'error',
                    error.message,
                    {
                        id: 'coupon-form',
                        type: 'snackbar',
                        context: 'wc/cart',
                    }
                );

                return Promise.resolve(false);
            });
    }

    const getValidPromotions = () => {
        return validPromotions ?? [];
    }
    const getAllPromotions = () => {
        return allPromotions ?? [];
    }

	return {
        isAnyPromotionApplied,
		isAnyVoucherApplied,
        isAnyGiftCardApplied,
		getFormType,
		removeAllVouchers,
        removeAllGiftCards,
        removeAllPromotions,
		addVoucherToList,
		addGiftCardToList,
        addPromotionsToList,
		displayGiftCardForm,
		displayRegularForm,
		getAppliedVouchersList,
		getAppliedGiftCardsList,
        getAppliedPromotionsList,
        giftCardInFlight,
		appliedVouchersList,
        appliedGiftCardsList,
        getValidPromotions,
        getAllPromotions,
        isLoading
	}
};
